import { FunctionComponent, useState } from 'react'
import { RequestSlot } from '@/types/Slots'
import { useTranslation } from 'react-i18next'
import { CalendarMonth, Check, ChildCareOutlined, Close, Delete, Message, Person } from '@mui/icons-material'
import moment from 'moment/moment'
import styles from './showRequestSlotDialog.module.scss'
import { DaycareType } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import ConfirmRequestSlot from '../../requestSlot/confirmRequestSlot/confirmRequestSlot'
import CancelSlotRequest from '../../requestSlot/cancelRequestSlot/cancelSlotRequest'
import { ChildRequestSlot } from '@/types/Child'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'
import { useAuth } from '@/modules/context/AuthContext'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { Role } from '@/types/Role'
import { useDaycareRight } from '@/hooks/useDaycareRight'
import { AuthContextType } from '@/types/Context'

interface ShowRequestSlotDialogProps {
    handleClose: () => void
    requestSlots: RequestSlot[]
    refetchData: () => void
}

const ShowRequestSlotDialog: FunctionComponent<ShowRequestSlotDialogProps> = ({
    handleClose,
    requestSlots,
    refetchData
}) => {
    const { t } = useTranslation()

    const user: AuthContextType = useAuth()

    const isParent: boolean = user.user?.role === Role.parent

    const canEditOccasional: boolean = useDaycareRight('canWriteOccasionalSlot')

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const navigate: NavigateFunction = useNavigate()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const [isCancelRequestSlotDialogOpen, setCancelRequestSlotDialogOpen] = useState<boolean>(false)
    const [isConfirmRequestSlotDialogOpen, setConfirmRequestSlotDialogOpen] = useState<boolean>(false)

    const childRequestSlots: ChildRequestSlot[] = requestSlots.map((slot: RequestSlot): ChildRequestSlot => {
        return {
            slotId: slot.id,
            childId: slot.childId,
            childName: slot.childName || '',
            childComplement: slot.childComplement || '',
            date: slot.date,
            start: slot.start,
            end: slot.end
        }
    })

    const [accepted, setAccepted] = useState<boolean>(false)

    function handleAcceptRequestSlot(): void {
        setAccepted(true)
        setConfirmRequestSlotDialogOpen(true)
    }

    function handleRefuseRequestSlot(): void {
        setAccepted(false)
        setConfirmRequestSlotDialogOpen(true)
    }

    return (
        <>
            {requestSlots[0].start && requestSlots[0].end && requestSlots[0].date && (
                <div className={styles.Container}>
                    <CalendarMonth className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <div className={styles.TextContainer}>
                        <p className={styles.FirstText}>
                            {format(parse(requestSlots[0].date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        </p>
                        <p className={styles.SecondText}>
                            {t('from') +
                                ' ' +
                                moment(requestSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                ' ' +
                                t('to').toLowerCase() +
                                ' ' +
                                moment(requestSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                        </p>
                    </div>
                </div>
            )}
            <div className={styles.BreakLine}></div>
            {isRpeOrLaep && requestSlots[0].parentName && (
                <div className={styles.SecondContainer}>
                    <Person
                        className={`${styles.Icon} ${isParent ? styles.IconParent : styles.ClickableIconDaycare}`}
                        onClick={() => navigate(`/adults?parent=${requestSlots[0].parentId}`)}
                    />
                    <p className={styles.Text}>{requestSlots[0].parentName}</p>
                </div>
            )}
            {requestSlots.length > 0 &&
                requestSlots.map((requestSlot: RequestSlot, index: number) => (
                    <div className={styles.SecondContainer} key={index}>
                        <ChildCareOutlined
                            className={`${styles.Icon} ${isParent ? styles.IconParent : styles.ClickableIconDaycare}`}
                            onClick={() => navigate(`/childs?child=${requestSlot.childId}`)}
                        />
                        <p className={styles.Text}>
                            {requestSlot.childName +
                                (requestSlot.childComplement ? ' - ' + requestSlot.childComplement : '')}
                        </p>
                    </div>
                ))}
            {requestSlots[0].comment && (
                <div className={styles.SecondContainer}>
                    <Message className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>{requestSlots[0].comment}</p>
                </div>
            )}
            <DialogActions>
                <ButtonTextIcon
                    type={'close'}
                    onClick={handleClose}
                    parent={isParent}
                    className={isParent ? styles.ButtonParent : styles.Button}
                    nowrap
                />
                {moment(requestSlots[0].date, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day') &&
                    (isParent ? (
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('cancel')}
                            customIcon={<Delete />}
                            onClick={() => setCancelRequestSlotDialogOpen(true)}
                            parent={isParent}
                            nowrap
                        />
                    ) : (
                        canEditOccasional && (
                            <>
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={() => handleAcceptRequestSlot()}
                                    customIcon={<Check />}
                                    customText={t('accept')}
                                    nowrap
                                />
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={() => handleRefuseRequestSlot()}
                                    customIcon={<Close />}
                                    customText={t('refuse')}
                                    nowrap
                                />
                            </>
                        )
                    ))}
            </DialogActions>
            {isConfirmRequestSlotDialogOpen && (
                <ConfirmRequestSlot
                    open={isConfirmRequestSlotDialogOpen}
                    accepted={accepted}
                    childRequestSlots={childRequestSlots}
                    currentDaycare={daycare}
                    handleClose={() => setConfirmRequestSlotDialogOpen(false)}
                    closeParentDialog={handleClose}
                    refetchData={refetchData}
                />
            )}
            {isCancelRequestSlotDialogOpen && (
                <CancelSlotRequest
                    handleClose={() => setCancelRequestSlotDialogOpen(false)}
                    childRequestSlots={childRequestSlots}
                    currentDaycare={daycare}
                    refetchData={refetchData}
                    closeParentDialog={handleClose}
                />
            )}
        </>
    )
}

export default ShowRequestSlotDialog
